import { useQuery } from "@tanstack/react-query";
import { api, fmtMoney, fmt } from "@/lib/api";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { TrendingUp, AlertTriangle, Wallet, Coins, HandCoins } from "lucide-react";
import { Link } from "react-router-dom";
import { StatusBadge } from "@/components/Table";

const CHART_COLORS = ["#1a3c2e", "#d4891a", "#2c5f49", "#e8a941", "#3a6b8c"];

function Kpi({ label, value, icon: Icon, tone = "forest", testId }) {
  return (
    <div data-testid={testId} className="card-soft p-5 animate-fade-up">
      <div className="flex items-start justify-between">
        <div>
          <div className="label-cap">{label}</div>
          <div className={`mt-3 text-3xl font-bold tracking-tight tabular-nums ${tone === "amber" ? "text-agri-amber-dark" : tone === "danger" ? "text-agri-danger" : "text-agri-forest"}`}>
            {value}
          </div>
        </div>
        <div className={`h-10 w-10 rounded-lg grid place-items-center ${tone === "amber" ? "bg-agri-amber/10 text-agri-amber" : tone === "danger" ? "bg-red-50 text-agri-danger" : "bg-agri-forest/10 text-agri-forest"}`}>
          <Icon size={18} />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api.get("/dashboard").then((r) => r.data),
    refetchInterval: 30000,
  });

  if (isLoading || !data) {
    return <div className="text-agri-ink-2" data-testid="dashboard-loading">Loading dashboard…</div>;
  }
  const { kpi, sales_30_days, top_products, payment_breakdown, recent_sales, low_stock_products } = data;

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-cap">Overview</div>
          <h1 className="font-heading text-3xl sm:text-4xl text-agri-ink mt-1">Today at a glance</h1>
        </div>
        <Link to="/pos" data-testid="dashboard-new-sale-btn" className="bg-agri-amber hover:bg-agri-amber-dark text-white text-sm font-medium px-4 py-2 rounded-lg shadow-sm transition-colors">
          + New Sale
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
        <Kpi testId="kpi-today-sales" label="Today's Sales" value={fmtMoney(kpi.today_sales)} icon={Coins} />
        <Kpi testId="kpi-outstanding" label="Receivable (Customers)" value={fmtMoney(kpi.outstanding_credit)} icon={Wallet} tone="amber" />
        <Kpi testId="kpi-payable" label="Payable (Suppliers)" value={fmtMoney(kpi.payable_suppliers)} icon={HandCoins} tone={kpi.payable_suppliers > 0 ? "danger" : "forest"} />
        <Kpi testId="kpi-low-stock" label="Low Stock Alerts" value={fmt(kpi.low_stock_count)} icon={AlertTriangle} tone={kpi.low_stock_count > 0 ? "danger" : "forest"} />
        <Kpi testId="kpi-net-profit" label="Monthly Net Profit" value={fmtMoney(kpi.net_profit)} icon={TrendingUp} tone={kpi.net_profit >= 0 ? "forest" : "danger"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="card-soft p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-cap">Trend</div>
              <h3 className="font-heading text-xl mt-1">Sales — Last 30 days</h3>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer>
              <LineChart data={sales_30_days}>
                <CartesianGrid stroke="rgba(28,28,30,0.06)" strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} stroke="#4a4a4d" fontSize={11} />
                <YAxis stroke="#4a4a4d" fontSize={11} tickFormatter={(v) => v >= 1000 ? `${v / 1000}k` : v} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid rgba(28,28,30,0.08)", borderRadius: 8 }} />
                <Line type="monotone" dataKey="sales" stroke="#1a3c2e" strokeWidth={2.5} dot={{ r: 2, fill: "#d4891a" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-soft p-5">
          <div className="label-cap">Payment Mix</div>
          <h3 className="font-heading text-xl mt-1 mb-3">This month</h3>
          <div className="h-72">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={payment_breakdown} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {payment_breakdown.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid rgba(28,28,30,0.08)", borderRadius: 8 }} />
                <Legend iconType="circle" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="card-soft p-5 lg:col-span-2">
          <div className="label-cap">Bestsellers</div>
          <h3 className="font-heading text-xl mt-1 mb-3">Top 10 products this month</h3>
          <div className="h-80">
            <ResponsiveContainer>
              <BarChart data={top_products} layout="vertical" margin={{ left: 30 }}>
                <CartesianGrid stroke="rgba(28,28,30,0.06)" strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" stroke="#4a4a4d" fontSize={11} />
                <YAxis type="category" dataKey="name" stroke="#4a4a4d" fontSize={11} width={140} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid rgba(28,28,30,0.08)", borderRadius: 8 }} />
                <Bar dataKey="qty" fill="#d4891a" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-soft p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-cap">Alerts</div>
              <h3 className="font-heading text-xl mt-1">Low stock</h3>
            </div>
            <Link to="/inventory?status=low" className="text-xs font-medium text-agri-amber-dark hover:underline">View all</Link>
          </div>
          <ul className="space-y-2" data-testid="low-stock-list">
            {low_stock_products.length === 0 && <li className="text-sm text-agri-ink-2">All products well-stocked.</li>}
            {low_stock_products.map((p) => (
              <li key={p.id} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-agri-bg transition-colors">
                <div>
                  <div className="text-sm font-medium text-agri-ink">{p.name}</div>
                  <div className="text-xs text-agri-ink-2">{p.category} · reorder at {p.reorder_level}</div>
                </div>
                <div className={`text-sm font-semibold tabular-nums ${p.stock_qty <= 0 ? "text-agri-danger" : "text-agri-amber-dark"}`}>
                  {p.stock_qty} {p.unit}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="card-soft p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-cap">Activity</div>
            <h3 className="font-heading text-xl mt-1">Recent transactions</h3>
          </div>
          <Link to="/sales" className="text-xs font-medium text-agri-amber-dark hover:underline">View all sales</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm rtable" data-testid="recent-sales-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-agri-ink-2 border-b border-[rgba(28,28,30,0.06)]">
                <th className="py-2 pr-4">Invoice</th>
                <th className="py-2 pr-4">Date</th>
                <th className="py-2 pr-4">Client</th>
                <th className="py-2 pr-4">Method</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {recent_sales.map((s) => (
                <tr key={s.id} className="border-b border-[rgba(28,28,30,0.04)] hover:bg-agri-bg/50">
                  <td className="py-3 pr-4 font-mono text-xs rt-primary">{s.invoice_no}</td>
                  <td data-label="Date" className="py-3 pr-4 text-agri-ink-2">{new Date(s.created_at).toLocaleString()}</td>
                  <td data-label="Client" className="py-3 pr-4">{s.client_name || <span className="text-agri-ink-2">Walk-in</span>}</td>
                  <td data-label="Method" className="py-3 pr-4 capitalize">{s.payment_method}</td>
                  <td data-label="Status" className="py-3 pr-4"><StatusBadge status={s.status} /></td>
                  <td data-label="Total" className="py-3 pr-4 text-right font-semibold tabular-nums">{fmtMoney(s.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
